import { BankAccount, Invoice } from '@/lib/types';
import { Payment, PaymentSetting } from '@/lib/types/payment';
import { FormInstance } from 'antd';
import { TFunction } from 'i18next';
import { useInPaymentInvoiceSync } from './useInPaymentInvoiceSync';
import { useInPaymentPageActions } from './useInPaymentPageActions';
import { useInPaymentPageValidation } from './useInPaymentPageValidation';

type UseInPaymentPageStateParams = {
  form: FormInstance;
  t: TFunction;
  bankAccounts: BankAccount[];
  invoices: { data: Invoice[] };
  invoicesIsLoading: boolean;
  paymentSettings: PaymentSetting[];
  paymentSettingsIsLoading: boolean;
  paymentsInProgress: Payment[] | undefined;
  refetchInvoices: () => Promise<unknown>;
  refetchPayments: () => Promise<unknown>;
  setPayableInvoiceCount: (value: number) => void;
};

type UseInPaymentPageStateResult = {
  checkedList: number[];
  invoiceIds: number[];
  errorMessages: string[];
  noCheckedBoxes: boolean;
  isCheckAll: boolean;
  isIndeterminate: boolean;
  sepaGenerateLoading: boolean;
  showErrorMessages: boolean;
  unusedCreditInvoices: Invoice[];
  onCheckAllChange: (e: { target: { checked: boolean } }) => void;
  onCheck: (id: number, checked: boolean) => void;
  handleFieldsChange: (
    changedFields: Array<{ name: Array<string | number> }>,
  ) => void;
  handleSubmit: () => Promise<void>;
  handleRemoveInvoice: (
    _invoiceIndex: number,
    invoice: Invoice,
  ) => Promise<void>;
  handleRemoveCreditInvoice: (
    _invoiceIndex: number,
    invoice: Invoice,
  ) => Promise<void>;
  handleSetIsInPaymentBatch: () => Promise<void>;
  refreshPaymentsInProgress: () => Promise<void>;
};

export const useInPaymentPageState = ({
  form,
  t,
  bankAccounts,
  invoices,
  invoicesIsLoading,
  paymentSettings,
  paymentSettingsIsLoading,
  paymentsInProgress,
  refetchInvoices,
  refetchPayments,
  setPayableInvoiceCount,
}: UseInPaymentPageStateParams): UseInPaymentPageStateResult => {
  const {
    checkedList,
    invoiceIds,
    unusedCreditInvoices,
    isCheckAll,
    isIndeterminate,
    noCheckedBoxes,
    setCheckedList,
    setInvoiceIds,
    onCheckAllChange,
    onCheck,
  } = useInPaymentInvoiceSync({
    form,
    bankAccounts,
    invoices,
    invoicesIsLoading,
    paymentSettings,
    paymentSettingsIsLoading,
    paymentsInProgress,
    refetchInvoices,
    setPayableInvoiceCount,
  });

  const {
    errorMessages,
    showErrorMessages,
    setShowErrorMessages,
    checkErrors,
    handleFieldsChange,
  } = useInPaymentPageValidation({
    form,
    t,
    checkedList,
  });

  const {
    sepaGenerateLoading,
    handleSubmit,
    handleSetIsInPaymentBatch,
    handleRemoveInvoice,
    handleRemoveCreditInvoice,
    refreshPaymentsInProgress,
  } = useInPaymentPageActions({
    form,
    t,
    checkedList,
    paymentsInProgress,
    refetchInvoices,
    refetchPayments,
    checkErrors,
    setShowErrorMessages,
    setCheckedList,
    setInvoiceIds,
  });

  return {
    checkedList,
    invoiceIds,
    errorMessages,
    noCheckedBoxes,
    isCheckAll,
    isIndeterminate,
    sepaGenerateLoading,
    showErrorMessages,
    unusedCreditInvoices,
    onCheckAllChange,
    onCheck,
    handleFieldsChange,
    handleSubmit,
    handleRemoveInvoice,
    handleRemoveCreditInvoice,
    handleSetIsInPaymentBatch,
    refreshPaymentsInProgress,
  };
};
